/**
 * Root application component.
 *
 * Sets up React Router v6 with BrowserRouter and wraps the entire tree in
 * AuthProvider so every descendant can access authentication state.
 *
 * Route map:
 *   /             → Home (public marketing landing)
 *   /login        → Login page (public; redirects to /dashboard when authenticated)
 *   /register     → Register page (public; creates org + owner, then → /company)
 *   /dashboard    → Dashboard (protected)
 *   /workers      → Workers: employees + contractors (protected; /employees redirects here)
 *   /payroll      → Payroll runs (protected)
 *   /taxes        → Tax forms + filing history (protected)
 *   /reports      → Payroll summary + employee earnings (protected)
 *   *             → redirect to /login
 *
 * @module App
 */

import { lazy, Suspense } from 'react';
import type { ComponentType, ReactElement } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ScrollToTop } from './components/ScrollToTop';
import { IdleTimeout } from './components/IdleTimeout';
import { SupportRoute } from './support/SupportRoute';

// Eager: the two first-paint entry points (marketing root + login) render
// without a second network round-trip.
import Home from './pages/Home';
import Login from './pages/Login';

// Everything else is route-level code-split (review P3-5): each page becomes
// its own chunk, so the employer app (PayrollRun/Taxes/Workers/…) and the
// support console no longer ship in the marketing bundle. Chunks are
// same-origin assets, so the strict production CSP is unaffected.
const Features = lazy(() => import('./pages/Features'));
const Pricing = lazy(() => import('./pages/Pricing'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Resources = lazy(() => import('./pages/Resources'));
const StateGuides = lazy(() => import('./pages/StateGuides'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AcceptableUse = lazy(() => import('./pages/AcceptableUse'));
const RestrictedBusinesses = lazy(() => import('./pages/RestrictedBusinesses'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
// DEV-gated at the import (not just the route) so the chunk isn't even
// emitted in production builds — it references Google Fonts, which the
// production CSP blocks.
const ThemePlayground: ComponentType = import.meta.env.DEV
  ? lazy(() => import('./pages/ThemePlayground'))
  : () => null;
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Workers = lazy(() => import('./pages/Workers'));
const PayrollRun = lazy(() => import('./pages/PayrollRun'));
const Funding = lazy(() => import('./pages/Funding'));
const Taxes = lazy(() => import('./pages/Taxes'));
const Reports = lazy(() => import('./pages/Reports'));
const CompanySetup = lazy(() => import('./pages/CompanySetup'));
const Conversion = lazy(() => import('./pages/Conversion'));
const GettingStarted = lazy(() => import('./pages/GettingStarted'));
const PortalHome = lazy(() => import('./pages/portal/PortalHome'));
const PortalProfile = lazy(() => import('./pages/portal/PortalProfile'));
const PortalOnboarding = lazy(() => import('./pages/portal/PortalOnboarding'));
const AccountSecurity = lazy(() => import('./pages/AccountSecurity'));
const Help = lazy(() => import('./pages/Help'));
const SupportLogin = lazy(() => import('./pages/support/SupportLogin'));
const SupportConsole = lazy(() => import('./pages/support/SupportConsole'));

/** Full-screen fallback while a route chunk loads (mirrors ProtectedRoute's). */
function PageLoader(): ReactElement {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-gray-500 text-sm">Loading...</div>
    </div>
  );
}

/**
 * Root application component.
 *
 * AuthProvider wraps the router so that ProtectedRoute and all page components
 * can access auth state without prop drilling.
 */
function App(): ReactElement {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <IdleTimeout />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public root — marketing homepage */}
          <Route path="/" element={<Home />} />

          {/* Public marketing pages */}
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/guides" element={<StateGuides />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          <Route path="/restricted-businesses" element={<RestrictedBusinesses />} />

          {/* Public: Login */}
          <Route path="/login" element={<Login />} />

          {/* Public: Register */}
          <Route path="/register" element={<Register />} />

          {/* Public: Self-service password reset */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Public: Accept employee portal invite */}
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* Unlinked design tool: live theme editor. DEV ONLY — it loads
              candidate fonts from Google Fonts, which the production CSP
              (vite.config.ts) deliberately blocks. */}
          {import.meta.env.DEV && <Route path="/theme" element={<ThemePlayground />} />}

          {/* Employee self-service portal (employee accounts) */}
          <Route
            path="/portal"
            element={
              <ProtectedRoute role="employee">
                <PortalHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/profile"
            element={
              <ProtectedRoute role="employee">
                <PortalProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/onboarding"
            element={
              <ProtectedRoute role="employee">
                <PortalOnboarding />
              </ProtectedRoute>
            }
          />

          {/* Protected: Help & support tickets (any signed-in user — employer or portal employee) */}
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <Help />
              </ProtectedRoute>
            }
          />

          {/* Protected: Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute role="employer">
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Protected: Workers (employees + contractors) */}
          <Route
            path="/workers"
            element={
              <ProtectedRoute role="employer">
                <Workers />
              </ProtectedRoute>
            }
          />
          {/* Legacy: the old employees page now lives at /workers */}
          <Route path="/employees" element={<Navigate to="/workers" replace />} />

          {/* Protected: Payroll Run */}
          <Route
            path="/payroll"
            element={
              <ProtectedRoute role="employer">
                <PayrollRun />
              </ProtectedRoute>
            }
          />

          {/* Protected: Funding (money movement — employer bank + per-run funding) */}
          <Route
            path="/funding"
            element={
              <ProtectedRoute role="employer">
                <Funding />
              </ProtectedRoute>
            }
          />

          {/* Protected: Taxes (forms + filing history) */}
          <Route
            path="/taxes"
            element={
              <ProtectedRoute role="employer">
                <Taxes />
              </ProtectedRoute>
            }
          />

          {/* Protected: Reports */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute role="employer">
                <Reports />
              </ProtectedRoute>
            }
          />

          {/* Protected: Account security (any logged-in user) */}
          <Route
            path="/security"
            element={
              <ProtectedRoute>
                <AccountSecurity />
              </ProtectedRoute>
            }
          />

          {/* Protected: Company Setup */}
          <Route
            path="/company"
            element={
              <ProtectedRoute role="employer">
                <CompanySetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company/conversion"
            element={
              <ProtectedRoute role="employer">
                <Conversion />
              </ProtectedRoute>
            }
          />
          <Route
            path="/getting-started"
            element={
              <ProtectedRoute role="employer">
                <GettingStarted />
              </ProtectedRoute>
            }
          />

          {/* Internal Ballast-staff support console (separate identity surface) */}
          <Route path="/support/login" element={<SupportLogin />} />
          <Route
            path="/support"
            element={
              <SupportRoute>
                <SupportConsole />
              </SupportRoute>
            }
          />

          {/* Catch-all: redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
